import { useEffect, useRef, useState, type ButtonHTMLAttributes, type HTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { APP_CATEGORY_LABELS, type AppCategory } from '@ward/core';
import { ApiError } from '../api';
import { ACCOUNT, ALERT_STATUS, APPROVAL, DECISION, EXT_STATUS, OUTCOME, PROTECTION, SEVERITY, statusText, statusTone, toneOf, type StatusMap, type Tone } from './format';

export function PageHead({ title, sub, crumbs, actions }: { title: ReactNode; sub?: ReactNode; crumbs?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        {crumbs && <div className="crumbs">{crumbs}</div>}
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Section({ title, aside, children, id }: { title: ReactNode; aside?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className="section" id={id}>
      <div className="section-head">
        <h2>{title}</h2>
        {aside && <div className="aside">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

export function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${90 - ((i * 13) % 35)}%` }} />
      ))}
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  const msg = error instanceof ApiError ? (error.status === 403 ? 'Your role does not allow this.' : error.message) : 'Something went wrong loading this data.';
  return <Notice tone="block" role="alert">{msg}</Notice>;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="state">
      <strong>{title}</strong>
      {children}
    </div>
  );
}

/** Renders loading / error / content for a query. */
export function Q<T>({ q, children, rows }: { q: { isLoading: boolean; error: unknown; data: T | undefined }; children: (d: T) => ReactNode; rows?: number }) {
  if (q.isLoading) return <Loading rows={rows} />;
  if (q.error) return <ErrorState error={q.error} />;
  if (q.data === undefined) return null;
  return <>{children(q.data)}</>;
}

export function MutationError({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error as ApiError;
  return (
    <Notice tone="block" role="alert">
      {e.status === 403 ? 'Your role does not allow this action.' : e.message}
      {e.details?.length ? (
        <ul className="notice-list">
          {e.details.map((d, i) => (
            <li key={i}>
              {d.path ? <code>{d.path}</code> : null} {d.message}
            </li>
          ))}
        </ul>
      ) : null}
    </Notice>
  );
}

// ── Formatting ─────────────────────────────────────────
function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = Math.round((Date.now() - Date.parse(iso)) / 1000);
  if (s < 0) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}
export function Ago({ at }: { at: string | null | undefined }) {
  return <time dateTime={at ?? undefined} title={at ? new Date(at).toLocaleString() : undefined} className="nowrap">{ago(at)}</time>;
}
export const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
export const fmtNum = (n: number | null | undefined) => (n ?? 0).toLocaleString();
export { browserLabel, osLabel, plural, outcomeTone, severityTone, statusText, statusTone, type StatusMap, type Tone } from './format';
export function fmtBytes(n: number | null | undefined) {
  if (n === null || n === undefined) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
export const catLabel = (c: string) => APP_CATEGORY_LABELS[c as AppCategory] ?? c;

// ── Status tags (always color + text) ───────────────────
// Labels and tones live in ./format (one vocabulary); these components only render them.

/** A tone-colored label. `dot={false}` drops the leading dot (for counts and secondary facts). */
export function Tag({ tone, dot = true, className, children, ...rest }: Omit<HTMLAttributes<HTMLSpanElement>, 'className'> & { tone?: Tone | string; dot?: boolean; className?: string; children: ReactNode }) {
  return <span className={['tag', dot ? '' : 'plain', className ?? ''].filter(Boolean).join(' ')} data-tone={toneOf(tone)} {...rest}>{children}</span>;
}

/** Renders `map[value]` as a tag; unknown values show the raw value in the neutral tone. */
export function StatusTag({ map, value, suffix, title }: { map: StatusMap; value: string; suffix?: string; title?: string }) {
  return <Tag tone={statusTone(map, value)} title={title}>{statusText(map, value)}{suffix}</Tag>;
}
/** Same as `<StatusTag>`, for call sites that build a tag from a lookup in an expression. */
export const tagFrom = (map: StatusMap, value: string) => <StatusTag map={map} value={value} />;

export function Outcome({ o }: { o: string }) {
  return <StatusTag map={OUTCOME} value={o} />;
}
export function DecisionTag({ d }: { d: string }) {
  return <StatusTag map={DECISION} value={d} />;
}
export function Severity({ s }: { s: string | null }) {
  if (!s) return <span className="muted">—</span>;
  return <StatusTag map={SEVERITY} value={s} />;
}
/** Extension risk level, on the severity scale: "High risk". */
export function Risk({ r }: { r: string }) {
  return <StatusTag map={SEVERITY} value={r} suffix=" risk" />;
}
export function Protection({ s }: { s: string }) {
  return <StatusTag map={PROTECTION} value={s} />;
}
export const PROTECTION_LABEL = (s: string) => statusText(PROTECTION, s);
export function AlertStatus({ s }: { s: string }) {
  return <StatusTag map={ALERT_STATUS} value={s} />;
}
export function Account({ t, domain }: { t: string; domain?: string | null }) {
  return (
    <span className="nowrap">
      <StatusTag map={ACCOUNT} value={t} />
      {domain ? <span className="sub"> {domain}</span> : null}
    </span>
  );
}
export function Approval({ a }: { a: string }) {
  return <StatusTag map={APPROVAL} value={a} />;
}
export function ExtStatus({ s }: { s: string }) {
  return <StatusTag map={EXT_STATUS} value={s} />;
}

// ── Notices, buttons, figures, layout ───────────────────

/** A boxed message: block = error, warn = caution, allow = success, info/neutral = context. */
export function Notice({ tone = 'neutral', className, children, ...rest }: Omit<HTMLAttributes<HTMLDivElement>, 'className'> & { tone?: Tone; className?: string; children: ReactNode }) {
  return <div className={className ? `notice ${className}` : 'notice'} data-tone={tone} {...rest}>{children}</div>;
}

type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
  /** primary = the one main action; secondary (default) = bordered; ghost = text-only. */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** danger = destructive (red text; red fill on primary). */
  tone?: 'danger';
  size?: 'small';
  className?: string;
};
/** `aria-pressed` buttons show their pressed state with a check mark and outline, never a solid fill. */
export function Button({ variant = 'secondary', tone, size, className, type = 'button', ...rest }: ButtonProps) {
  const cls = ['btn', variant === 'secondary' ? '' : variant, tone ?? '', size ?? '', className ?? ''].filter(Boolean).join(' ');
  return <button type={type} className={cls} {...rest} />;
}

/** A strip of headline numbers (a ruled ledger). Children are `<Figure>`s. */
export function Figures({ label, children }: { label?: string; children: ReactNode }) {
  return <dl className="ledger" aria-label={label}>{children}</dl>;
}
/**
 * One headline number. Ink-colored unless `tone` is set; `href` makes it a link and `onClick` a toggle button
 * (`pressed` marks the active one). `unit` is small text after the number, `sub` a line under it.
 */
export function Figure({ label, value, unit, sub, tone, href, onClick, pressed, size }: {
  label: ReactNode; value: ReactNode; unit?: ReactNode; sub?: ReactNode; tone?: Tone; href?: string; onClick?: () => void; pressed?: boolean; size?: 'small';
}) {
  const body = href ? <Link className="figure-link" to={href}>{value}</Link>
    : onClick ? <button type="button" className="figure-link" aria-pressed={pressed} onClick={onClick}>{value}</button>
    : value;
  return (
    <div>
      <dt>{label}</dt>
      <dd data-tone={tone} className={size === 'small' ? 'figure-small' : undefined}>{body}{unit != null && <small>{unit}</small>}</dd>
      {sub != null && <div className="delta">{sub}</div>}
    </div>
  );
}

/**
 * Vertical (default) or horizontal (`row`: wraps, centered) stack with a gap from the spacing scale (px).
 * Same as the `.stack-<gap>` / `.row-<gap>` classes.
 */
export function Stack({ gap = 8, row, align, className, children, as: As = 'div' }: {
  gap?: 2 | 4 | 8 | 12 | 16 | 24 | 32; row?: boolean; align?: 'start' | 'baseline'; className?: string; children: ReactNode; as?: 'div' | 'ul' | 'ol' | 'section';
}) {
  const cls = [`${row ? 'row' : 'stack'}-${gap}`, align ? `items-${align}` : '', className ?? ''].filter(Boolean).join(' ');
  return <As className={cls}>{children}</As>;
}

export function Seg<T extends string>({ value, options, onChange, label }: { value: T; options: Array<[T, string]>; onChange: (v: T) => void; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map(([v, l]) => (
        <button key={v} type="button" aria-pressed={v === value} onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </div>
  );
}

/** Rows per page in every console list. */
export const PAGE_SIZE = 50;

export function Pager({ total, limit, offset, onChange }: { total: number; limit: number; offset: number; onChange: (o: number) => void }) {
  if (total <= limit) return null;
  return (
    <div className="pager">
      <span>
        {offset + 1}–{Math.min(total, offset + limit)} of {total >= 10000 ? '10,000+' : fmtNum(total)}
      </span>
      <button className="btn small" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>Previous</button>
      <button className="btn small" disabled={offset + limit >= total} onClick={() => onChange(offset + limit)}>Next</button>
    </div>
  );
}

/**
 * List filters kept in the URL. `set(k, v)` sets or clears one filter (replacing the history entry) and, unless
 * `resetOffset` is false, returns to the first page; `setOffset` is the Pager's onChange (adds a history entry).
 */
export function useFilters({ resetOffset = true }: { resetOffset?: boolean } = {}) {
  const [sp, setSp] = useSearchParams();
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    if (resetOffset) n.delete('offset');
    setSp(n, { replace: true });
  };
  const setOffset = (o: number) => {
    const n = new URLSearchParams(sp);
    n.set('offset', String(o));
    setSp(n);
  };
  /** Removes every filter (and the page and sort) except the `keep` keys. */
  const clear = (keep: readonly string[] = []) => {
    const n = new URLSearchParams();
    for (const k of keep) if (sp.get(k)) n.set(k, sp.get(k)!);
    setSp(n, { replace: true });
  };
  /** True when any filter other than paging, sorting, an open drawer and the `keep` keys is set. */
  const active = (keep: readonly string[] = []) => [...sp.entries()].some(([k, v]) => v && !['offset', 'sort', 'event', ...keep].includes(k));
  return { sp, setSp, set, offset: Number(sp.get('offset') ?? 0), setOffset, clear, active, sort: sp.get('sort') ?? '' };
}
export type Filters = ReturnType<typeof useFilters>;

/**
 * The toolbar above every list: free-text search (URL key `search`, default `q`; `false` for none), the list's own
 * filter controls, and "Clear filters" once anything is set. `keep` names URL keys that are views, not filters.
 */
export function ListToolbar({ filters, search = 'q', placeholder = 'Search…', keep = [], children }: {
  filters: Filters; search?: string | false; placeholder?: string; keep?: readonly string[]; children?: ReactNode;
}) {
  return (
    <div className="filters">
      {search !== false && <Search value={filters.sp.get(search) ?? ''} onChange={(v) => filters.set(search, v)} placeholder={placeholder} />}
      {children}
      {filters.active(keep) && <Button size="small" variant="ghost" onClick={() => filters.clear(keep)}>Clear filters</Button>}
    </div>
  );
}

/** A removable filter shown in a ListToolbar, e.g. "User: Jane Doe ×" for a filter set by a link. */
export function FilterChip({ label, onRemove }: { label: ReactNode; onRemove: () => void }) {
  return (
    <span className="chip">
      {label}
      <button type="button" aria-label="Remove filter" onClick={onRemove}>×</button>
    </span>
  );
}

/**
 * Sortable column header. `col` is the server's sort key; the first click sorts newest first (`-col`).
 * `defaultDir` marks the column the list is sorted by when no `sort` is set.
 */
export function SortTh({ filters, col, children, defaultDir, className }: { filters: Filters; col: string; children: ReactNode; defaultDir?: 'asc' | 'desc'; className?: string }) {
  const cur = filters.sort || (defaultDir === 'asc' ? col : defaultDir === 'desc' ? `-${col}` : '');
  const dir = cur === col ? 'ascending' : cur === `-${col}` ? 'descending' : undefined;
  return (
    <th aria-sort={dir} className={className}>
      <button type="button" className="th-sort" onClick={() => filters.set('sort', dir === 'descending' ? col : `-${col}`)}>
        {children}<span aria-hidden="true">{dir === 'ascending' ? '↑' : dir === 'descending' ? '↓' : '↕'}</span>
      </button>
    </th>
  );
}

/** A small menu behind a button (e.g. rarely used or destructive header actions). Items are `<MenuItem>`s. */
export function Menu({ label, children }: { label: ReactNode; children: ReactNode }) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent | globalThis.KeyboardEvent) => {
      if (!ref.current?.open) return;
      if (e instanceof MouseEvent ? !ref.current.contains(e.target as Node) : e.key === 'Escape') ref.current.removeAttribute('open');
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', close);
    };
  }, []);
  return (
    <details className="menu" ref={ref}>
      <summary className="btn">{label}</summary>
      <div className="menu-pop" role="menu" onClick={() => ref.current?.removeAttribute('open')}>{children}</div>
    </details>
  );
}
export function MenuItem({ onClick, danger, children }: { onClick: () => void; danger?: boolean; children: ReactNode }) {
  return <button type="button" role="menuitem" className={danger ? 'menu-item danger' : 'menu-item'} onClick={onClick}>{children}</button>;
}

/** Debounced text input for search filters. */
export function Search({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const [v, setV] = useState(value);
  const t = useRef<number>();
  useEffect(() => setV(value), [value]);
  return (
    <input
      type="search"
      className="grow"
      aria-label={placeholder}
      placeholder={placeholder}
      value={v}
      onChange={(e) => {
        setV(e.target.value);
        window.clearTimeout(t.current);
        t.current = window.setTimeout(() => onChange(e.target.value), 250);
      }}
    />
  );
}

/** Props for a table row that opens something on click or Enter (spread onto the <tr>). */
export function clickableRow(go: () => void) {
  return { className: 'clickable', tabIndex: 0, onClick: go, onKeyDown: (k: KeyboardEvent) => k.key === 'Enter' && go() };
}

/** Link inside a clickableRow: navigates without also triggering the row. */
export function EntityLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}
